import { useTranslation } from 'react-i18next';
import { Flag, Users } from 'lucide-react';
import RatingStars from '../common/RatingStars';
import { FreelancerSearchResult } from '../../types/search';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import ProfileAvatar from '../common/ProfileAvatar';
import { twMerge } from 'tailwind-merge';
import { Button } from '../common/Button';
import { Link } from 'react-router-dom';
import { useProfileRole } from '../../hooks/useProfileRole';
import EyeButton from '../common/EyeButton';

interface FreelancerCardProps {
  freelancer: FreelancerSearchResult;
  onClick?: () => void;
  complete?: boolean;
  className?: string;
  alternate?: boolean;
  showEyeIcon?: boolean;
}

export default function FreelancerCard({
  freelancer,
  onClick,
  complete = false,
  className,
  showEyeIcon = false,
}: FreelancerCardProps) {
  const { t } = useTranslation(['freelancers', 'common', 'projects']);
  const subtitle = getFreelancerSubtitle(freelancer, t);
  const distance =
    freelancer.distance !== undefined && freelancer.distance !== null
      ? `${Math.round(freelancer.distance)} km`
      : '-';
  const allianceCount = freelancer.alliance_count > 10 ? '10+' : freelancer.alliance_count || 0;

  const { isAdmin } = useProfileRole();

  return (
    <div
      className={twMerge(
        `
    relative
    grid grid-cols-[3.5rem_minmax(0,1fr)] 
    md:grid-cols-[50px_minmax(0,1fr)_auto] 
    gap-4 w-full max-w-full overflow-hidden 
    p-5 bg-white rounded-2xl border-2 border-gray-300 shadow-xl hover:cursor-pointer
    md:p-0 md:bg-transparent md:border-0 md:shadow-none md:items-center
    `,
        className
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.();
      }}
    >
      <ProfileAvatar
        profile_id={freelancer.id}
        first_name={freelancer.first_name}
        avatar_url={freelancer.avatar_url}
        className="h-14 w-14 flex-shrink-0 md:h-[50px] md:w-[50px]"
      />

      <div className="flex w-full min-w-0 flex-col gap-y-3 md:w-auto md:flex-row md:gap-8">
        <div className="col-span-2 md:col-span-1 flex w-full min-w-0 flex-col md:flex-row justify-between">
          <div className="min-w-0 text-secondary mt-2 md:mt-0">
            <div className="flex items-start gap-2 justify-between">
              <div className="truncate text-xl font-bold leading-tight md:text-base">
                {freelancer.first_name}
              </div>
              <Link
                to={
                  isAdmin ? `/admin/freelancers/${freelancer.id}` : `/freelancers/${freelancer.id}`
                }
                onClick={e => e.stopPropagation()}
                className="flex-shrink-0 md:hidden"
              >
                {showEyeIcon ? (
                  <EyeButton />
                ) : (
                  <Button
                    variant="transparent-with-border"
                    className="inline-flex rounded-full px-6 py-3 !text-base md:px-4 md:py-2 md:!text-sm"
                  >
                    {t('viewProfile', { ns: 'freelancers' })}
                  </Button>
                )}
              </Link>
            </div>
            <div className="truncate text-base leading-tight md:text-sm mt-6">{subtitle}</div>

            {complete && <div className="hidden whitespace-nowrap text-xs">0km</div>}
          </div>
        </div>

        {complete && (
          <div className="-ml-[4.5rem] flex flex-col w-[calc(100%+4.5rem)] items-start justify-between md:ml-0 md:w-full md:flex-row md:items-baseline md:justify-start md:gap-1">
            <span className="truncate text-base text-secondary md:text-sm">{freelancer.city}</span>
            <span className="ml-0 mt-1 whitespace-nowrap text-base font-semibold md:ml-4 md:mt-0 md:whitespace-normal md:text-sm md:font-normal md:text-secondary">
              {distance}
            </span>
          </div>
        )}

        {complete && (
          <div className="-ml-[4.5rem] grid w-[calc(100%+4.5rem)] grid-cols-1 gap-4 md:ml-0 md:grid-cols-3 md:flex md:w-auto md:flex-row md:gap-8">
            <div className="text-left md:text-start">
              <span className="mb-2 block text-sm text-secondary">
                {t('rating', { ns: 'freelancers' })}
              </span>
              <RatingStars
                rating={freelancer.overall_rating}
                starClassName="h-4 w-4 md:h-3 md:w-3"
              />
            </div>

            <div className="text-left md:text-start">
              <span className="mb-2 block text-sm text-secondary">
                {t('alliances', { ns: 'freelancers' })}
              </span>
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary md:h-5 md:w-5">
                  <span className="text-xs text-white">{allianceCount}</span>
                </div>
                <Users />
              </div>
            </div>

            <div className="text-left md:text-start">
              <span className="mb-2 block text-sm text-secondary">
                {t('title', { ns: 'projects' })}
              </span>
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary md:h-5 md:w-5">
                  <span className="text-xs text-white">{freelancer.projects_count ?? 0}</span>
                </div>
                <Flag />
              </div>
            </div>
          </div>
        )}
      </div>

      <span className="hidden col-span-2 mt-4 md:col-span-1 md:ml-0 md:mt-0 md:flex md:items-center md:justify-center md:justify-self-end">
        <Link
          to={isAdmin ? `/admin/freelancers/${freelancer.id}` : `/freelancers/${freelancer.id}`}
          onClick={e => e.stopPropagation()}
        >
          {showEyeIcon ? (
            <EyeButton />
          ) : (
            <Button
              variant="transparent-with-border"
              className="inline-flex rounded-full px-6 py-3 !text-base md:px-4 md:py-2 md:!text-sm"
            >
              {t('viewProfile', { ns: 'freelancers' })}
            </Button>
          )}
        </Link>
      </span>
    </div>
  );
}
